import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { currentLang, type Doc, type DocSet, loadDocs, search } from '@/lib/docs';
import {
  CookieBanner, cookiePolicyUrl, initAnalytics, LanguageMenu, resolveAnalytics, ThemeMenu,
} from '@easydent/shared-ui';
import { languageOptions, setLang, UI, type UiStrings } from '@/lib/ui';

/**
 * The clinic app links here with `?return=<url>`, so "Back to easyDent" goes to the screen the
 * reader left rather than a generic home page. Only https URLs on our own domains are honoured —
 * an open redirect is still an open redirect when the page is only documentation.
 */
function returnUrl(): string | null {
  const raw = new URLSearchParams(window.location.search).get('return');
  if (!raw) return null;
  try {
    const url = new URL(raw);
    // easydent365.com is easyDent's own product domain, and where these guides now live. Without
    // it, "Back to easyDent" from any production app would fail the check and be silently dropped —
    // the reader would simply lose the way back, with nothing to indicate why.
    const ok = url.protocol === 'https:'
      && /(^|\.)easysoft\.ro$|(^|\.)edentisti\.ro$|(^|\.)easydent365\.com$/.test(url.hostname);
    return ok ? url.toString() : null;
  } catch {
    return null;
  }
}

const Markdown: React.FC<{ children: string }> = ({ children }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      h1: (p) => <h1 className="mb-3 text-3xl font-semibold text-body" {...p} />,
      h2: (p) => <h2 className="mt-10 mb-3 border-b border-line pb-2 text-xl font-semibold text-body" {...p} />,
      h3: (p) => <h3 className="mt-6 mb-2 text-base font-semibold text-body" {...p} />,
      p: (p) => <p className="my-3 leading-7 text-body-soft" {...p} />,
      ul: (p) => <ul className="my-3 list-disc space-y-1 pl-6 text-body-soft" {...p} />,
      ol: (p) => <ol className="my-3 list-decimal space-y-1 pl-6 text-body-soft" {...p} />,
      li: (p) => <li className="leading-7" {...p} />,
      strong: (p) => <strong className="font-semibold text-body" {...p} />,
      a: (p) => <a className="text-brand-ink underline hover:text-brand-ink-strong" {...p} />,
      blockquote: (p) => (
        <blockquote className="my-4 rounded-r border-l-4 border-brand-300 bg-brand-50 px-4 py-3 text-body-soft" {...p} />
      ),
      code: (p) => <code className="rounded bg-subtle px-1.5 py-0.5 text-sm text-body-strong" {...p} />,
      table: (p) => (
        <div className="my-4 overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm" {...p} />
        </div>
      ),
      th: (p) => <th className="border-b border-line-strong px-3 py-2 font-semibold text-body" {...p} />,
      td: (p) => <td className="border-b border-line px-3 py-2 text-body-soft" {...p} />,
    }}
  >
    {children}
  </ReactMarkdown>
);

const DocPage: React.FC<{ set: DocSet; t: UiStrings }> = ({ set, t }) => {
  const { slug } = useParams<{ slug: string }>();
  const doc = set.docs.find((d) => d.slug === slug);

  if (!doc) {
    return (
      <div>
        <h1 className="mb-3 text-2xl font-semibold text-body">{t.notFoundTitle}</h1>
        <p className="text-muted-strong">
          {t.notFoundBody} <Link className="text-brand-ink underline" to="/">{t.notFoundLink}</Link>.
        </p>
      </div>
    );
  }

  const next: Doc | undefined = set.docs[set.docs.findIndex((d) => d.slug === doc.slug) + 1];

  return (
    <article>
      {set.fallbacks.has(doc.slug) && (
        <p className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t.fallbackNotice}
        </p>
      )}
      <Markdown>{doc.body}</Markdown>
      {next && (
        <div className="mt-12 border-t border-line pt-5">
          <span className="text-xs tracking-wide text-muted uppercase">{t.next}</span>
          <Link className="mt-1 block text-brand-ink hover:underline" to={`/${next.slug}`}>
            {next.title} →
          </Link>
        </div>
      )}
    </article>
  );
};

const Home: React.FC<{ set: DocSet; t: UiStrings }> = ({ set, t }) => (
  <div>
    <h1 className="mb-2 text-3xl font-semibold text-body">{t.homeTitle}</h1>
    <p className="mb-8 text-muted-strong">{t.homeIntro}</p>
    {set.sections.map((section) => (
      <section key={section.name} className="mb-8">
        <h2 className="mb-3 text-xs font-semibold tracking-wide text-muted uppercase">{section.name}</h2>
        <ul className="space-y-2">
          {section.docs.map((doc) => (
            <li key={doc.slug}>
              <Link
                to={`/${doc.slug}`}
                className="block rounded-lg border border-line p-4 transition hover:border-brand-300 hover:bg-brand-50"
              >
                <span className="font-medium text-body">{doc.title}</span>
                {doc.summary && <span className="mt-0.5 block text-sm text-muted-strong">{doc.summary}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    ))}
  </div>
);

const App: React.FC = () => {
  const [set, setSet] = useState<DocSet | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');

  const lang = set?.lang ?? currentLang();
  const t = UI[lang] ?? UI.en;

  // Consent-gated analytics. Stays off until this app has a website id AND the reader consents.
  useEffect(() => {
    initAnalytics(resolveAnalytics('docs'));
  }, []);

  useEffect(() => {
    let live = true;
    loadDocs(currentLang())
      .then((loaded) => live && setSet(loaded))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const hits = useMemo(() => (set ? search(set.docs, query) : []), [set, query]);
  const back = returnUrl();

  return (
    <div className="min-h-screen bg-surface text-body-strong">
      <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3">
          <Link to="/" className="text-lg font-semibold text-brand-ink-strong">
            easyDent <span className="font-normal text-muted">{t.brand}</span>
          </Link>

          <div className="relative ml-auto w-full max-w-sm">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              aria-label={t.searchPlaceholder}
              disabled={!set}
              className="w-full rounded-lg border border-line-strong px-3 py-1.5 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-canvas"
            />
            {query.trim().length >= 2 && (
              <div className="absolute right-0 left-0 mt-1 max-h-96 overflow-y-auto rounded-lg border border-line bg-surface shadow-lg">
                {hits.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-muted">{t.searchEmpty(query)}</p>
                ) : (
                  hits.map((hit) => (
                    <Link
                      key={hit.doc.slug}
                      to={`/${hit.doc.slug}`}
                      onClick={() => setQuery('')}
                      className="block border-b border-line-soft px-3 py-2 last:border-0 hover:bg-brand-50"
                    >
                      <span className="block text-sm font-medium text-body">{hit.doc.title}</span>
                      {hit.snippet && <span className="block text-xs text-muted">{hit.snippet}</span>}
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>

          {/* The same component the clinic app uses, so the control looks and behaves identically
              on both sides of the link. */}
          <LanguageMenu
            value={lang}
            languages={languageOptions()}
            onChange={setLang}
            aria-label={t.languageLabel}
          />
          <ThemeMenu labels={{ label: t.themeLabel, light: t.themeLight, dark: t.themeDark, system: t.themeSystem }} />

          {back && (
            <a href={back} className="shrink-0 text-sm font-medium text-brand-ink hover:underline">
              {t.back}
            </a>
          )}
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-10 px-4 py-8">
        <nav className="hidden w-60 shrink-0 md:block" aria-label={t.navLabel}>
          {set?.sections.map((section) => (
            <div key={section.name} className="mb-6">
              <p className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">{section.name}</p>
              <ul className="space-y-1">
                {section.docs.map((doc) => (
                  <li key={doc.slug}>
                    <NavLink
                      to={`/${doc.slug}`}
                      className={({ isActive }) =>
                        `block rounded px-2 py-1 text-sm ${
                          isActive ? 'bg-brand-50 font-medium text-brand-ink-strong' : 'text-muted-strong hover:text-body'
                        }`
                      }
                    >
                      {doc.title}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <main className="min-w-0 flex-1">
          {failed ? (
            <p className="text-muted-strong">{t.loadFailed}</p>
          ) : !set ? (
            <p className="text-subtle-ink">{t.loading}</p>
          ) : (
            <Routes>
              <Route path="/" element={<Home set={set} t={t} />} />
              <Route path="/:slug" element={<DocPage set={set} t={t} />} />
            </Routes>
          )}
        </main>
      </div>

      <footer className="mx-auto max-w-6xl px-4 py-10 text-sm text-muted">
        {t.footer} <strong className="font-medium text-body-soft">Help</strong> {t.footerHelp}
      </footer>

      {/*
        Labels passed straight through rather than via `cookieBannerLabels`: that helper adapts an
        i18next `t`, and this app deliberately has none. The strings are already resolved for the
        current language in `UI`.

        The policy link goes to the public site — these pages are served from `docs.<domain>` and
        have no legal route of their own.
      */}
      <CookieBanner labels={t.cookieBanner} cookiePolicyHref={cookiePolicyUrl()} />
    </div>
  );
};

export default App;
